import React from "react";
import { Models } from "@hex-insights/app-modules";
import { makeClassName } from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import { SocialReaction } from "../../../../Utilities";
import { SocialReactionFormValues, SocialReactionMutation } from "../../../FormUtilities";
import styles from "./styles.module.css";

type SocialReactionToggleButtonProps<
	K extends keyof SocialReactionFormValues.Create = keyof SocialReactionFormValues.Create,
> = {
	instanceID?: SocialReaction["id"] | null;
	formValues: SocialReactionFormValues.CreateSubset<K>;
	iconSize?: number;
} & Omit<Models.ActionButtonProps, "performAction" | "noNotifications" | "title" | "children">;

export function SocialReactionToggleButton<
	K extends keyof SocialReactionFormValues.Create = keyof SocialReactionFormValues.Create,
>({ instanceID, formValues, ...props }: SocialReactionToggleButtonProps<K>) {
	if (instanceID) {
		return <Delete instanceID={instanceID} {...props} noNotifications title="Remove like" />;
	}
	return <Create formValues={formValues} {...props} noNotifications title="Like" />;
}

type CreateProps<K extends keyof SocialReactionFormValues.Create = keyof SocialReactionFormValues.Create> = {
	formValues: SocialReactionFormValues.CreateSubset<K>;
	iconSize?: number;
} & Omit<Models.ActionButtonProps, "performAction">;

function Create<K extends keyof SocialReactionFormValues.Create = keyof SocialReactionFormValues.Create>({
	formValues,
	iconSize = 16,
	className,
	style,
	...props
}: CreateProps<K>) {
	const [wasClicked, setWasClicked] = React.useState(false);

	const create = SocialReactionMutation.useCreate<K>();
	const applyCreate = React.useCallback(async () => {
		setWasClicked(true);
		create(formValues);
		return {};
	}, [create, formValues]);

	return (
		<Models.ActionButton
			performAction={applyCreate}
			className={makeClassName(styles["create-button"], wasClicked ? styles["create-button--clicked"] : "", className)}
			style={{ padding: 0, ...style }}
			{...props}
		>
			<Icon.Heart size={iconSize} weight={wasClicked ? "fill" : undefined} className="block" />
		</Models.ActionButton>
	);
}

type DeleteProps = {
	instanceID: SocialReaction["id"];
	iconSize?: number;
} & Omit<Models.ActionButtonProps, "performAction">;

function Delete({ instanceID, iconSize = 16, className, style, ...props }: DeleteProps) {
	const [wasClicked, setWasClicked] = React.useState(false);

	const del = SocialReactionMutation.useDelete(instanceID);
	const applyDelete = React.useCallback(async () => {
		setWasClicked(true);
		del();
		return {};
	}, [del]);

	return (
		<Models.ActionButton
			performAction={applyDelete}
			className={makeClassName(styles["delete-button"], wasClicked ? styles["delete-button--clicked"] : "", className)}
			style={{ padding: 0, ...style }}
			{...props}
		>
			<Icon.Heart size={iconSize} weight={wasClicked ? undefined : "fill"} className="block" />
		</Models.ActionButton>
	);
}
